import React from 'react';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import { Plus } from 'lucide-react';
import { TimeRangePicker } from './TimeRangePicker';
import { cn } from '@/lib/utils';

export interface TimeRange {
  id: string;
  start_time: string;
  end_time: string;
}

interface DayAvailabilityRowProps {
  dayLabel: string;
  dayShort: string;
  isAvailable: boolean;
  timeRanges: TimeRange[];
  onToggle: (available: boolean) => void;
  onAddTimeRange: () => void;
  onRemoveTimeRange: (id: string) => void;
  onTimeRangeChange: (id: string, field: 'start_time' | 'end_time', value: string) => void;
  disabled?: boolean;
  className?: string;
}

export const DayAvailabilityRow: React.FC<DayAvailabilityRowProps> = ({
  dayLabel,
  dayShort,
  isAvailable,
  timeRanges,
  onToggle,
  onAddTimeRange,
  onRemoveTimeRange,
  onTimeRangeChange,
  disabled = false,
  className,
}) => {
  return (
    <div
      className={cn(
        'flex flex-col sm:flex-row sm:items-start gap-3 p-4 rounded-xl border transition-all',
        isAvailable ? 'bg-white border-slate-200' : 'bg-slate-50 border-slate-100',
        className
      )}
    >
      {/* Day toggle */}
      <div className="flex items-center gap-3 min-w-[140px]">
        <Switch
          checked={isAvailable}
          onCheckedChange={onToggle}
          disabled={disabled}
        />
        <span
          className={cn(
            'font-medium text-sm',
            isAvailable ? 'text-slate-900' : 'text-slate-400'
          )}
        >
          <span className="hidden sm:inline">{dayLabel}</span>
          <span className="sm:hidden">{dayShort}</span>
        </span>
      </div>

      {/* Time ranges or unavailable message */}
      <div className="flex-1">
        {isAvailable ? (
          <div className="space-y-2">
            {timeRanges.map((range, index) => (
              <TimeRangePicker
                key={range.id}
                startTime={range.start_time}
                endTime={range.end_time}
                onStartTimeChange={(time) => onTimeRangeChange(range.id, 'start_time', time)}
                onEndTimeChange={(time) => onTimeRangeChange(range.id, 'end_time', time)}
                onRemove={() => onRemoveTimeRange(range.id)}
                showRemove={timeRanges.length > 1}
                disabled={disabled}
              />
            ))}

            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={onAddTimeRange}
              disabled={disabled}
              className="text-teal-600 hover:text-teal-700 hover:bg-teal-50 h-8 px-2"
            >
              <Plus className="h-4 w-4 mr-1" />
              Add hours
            </Button>
          </div>
        ) : (
          <span className="text-sm text-slate-400 italic">Unavailable</span>
        )}
      </div>
    </div>
  );
};

export default DayAvailabilityRow;







